import type { ComponentType, CSSProperties, SVGProps } from 'react';
import { ICON_PATHS, type IconName, type IconWeight } from './icon-paths';

/**
 * The app's icon set, in one place. Components import icons by these names;
 * swapping the look only means editing this file.
 *
 * Drawings come from Phosphor (MIT). Only the weights we use are bundled:
 * `npm run icons` reads the names below and regenerates ./icon-paths.ts.
 * Icons are decorative unless given an aria-label.
 */

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'name'> {
  size?: number;
  weight?: IconWeight;
  /** Accepted for compatibility; weight controls thickness. */
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}
export type AppIcon = ComponentType<IconProps>;

const DEFAULT_WEIGHT: IconWeight = 'bold';

function Glyph({ name, weight, size, ...rest }: { name: IconName; weight: IconWeight; size: number } & Omit<IconProps, 'weight' | 'size'>) {
  const paths = ICON_PATHS[name] as Partial<Record<IconWeight, string>>;
  const labelled = rest['aria-label'] !== undefined;
  const { strokeWidth: _strokeWidth, ...svgProps } = rest;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width={size} height={size} fill="currentColor" aria-hidden={labelled ? undefined : true} {...svgProps}>
      <path d={paths[weight] ?? paths.bold} />
    </svg>
  );
}

function icon(name: IconName, defaults: { weight?: IconWeight } = {}): AppIcon {
  return function Icon({ size = 24, weight, ...rest }: IconProps) {
    return <Glyph name={name} weight={weight ?? defaults.weight ?? DEFAULT_WEIGHT} size={size} {...rest} />;
  };
}

/** A base icon with a diagonal "off" slash cut through it. */
function slashed(name: IconName): AppIcon {
  return function Slashed({ size = 24, weight, strokeWidth: _strokeWidth, className, style, ...rest }: IconProps) {
    const labelled = rest['aria-label'] !== undefined;
    const cut = 'linear-gradient(45deg, #000 43%, transparent 43%, transparent 57%, #000 57%)';
    return (
      <span
        role={labelled ? 'img' : undefined}
        aria-label={rest['aria-label']}
        aria-hidden={labelled ? undefined : true}
        className={className}
        style={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0, ...style }}
      >
        <Glyph name={name} weight={weight ?? DEFAULT_WEIGHT} size={size} style={{ WebkitMaskImage: cut, maskImage: cut }} />
        <svg viewBox="0 0 24 24" width={size} height={size} style={{ position: 'absolute', inset: 0 }} aria-hidden>
          <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" />
        </svg>
      </span>
    );
  };
}

/** A base icon with a small "+" badge in its corner. */
function plussed(name: IconName): AppIcon {
  return function Plussed({ size = 24, weight, strokeWidth: _strokeWidth, className, style, ...rest }: IconProps) {
    const labelled = rest['aria-label'] !== undefined;
    const cut = 'radial-gradient(circle at 84% 84%, transparent 30%, #000 31%)';
    return (
      <span
        role={labelled ? 'img' : undefined}
        aria-label={rest['aria-label']}
        aria-hidden={labelled ? undefined : true}
        className={className}
        style={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0, ...style }}
      >
        <Glyph name={name} weight={weight ?? DEFAULT_WEIGHT} size={size} style={{ WebkitMaskImage: cut, maskImage: cut }} />
        <svg viewBox="0 0 24 24" width={size} height={size} style={{ position: 'absolute', inset: 0 }} aria-hidden>
          <path d="M20 15.5v8M16 19.5h8" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" />
        </svg>
      </span>
    );
  };
}

export const AlertTriangle = icon('Warning');
export const ArrowDown = icon('ArrowDown');
export const ArrowLeft = icon('ArrowLeft');
export const ArrowUp = icon('ArrowUp');
export const AtSign = icon('At');
export const BadgeCheck = icon('SealCheck', { weight: 'fill' });
export const Ban = icon('Prohibit');
export const Bell = icon('Bell');
export const BellOff = icon('BellSlash');
export const Check = icon('Check');
export const CheckCheck = icon('Checks');
export const CheckCircle2 = icon('CheckCircle');
export const ChevronDown = icon('CaretDown');
export const ChevronRight = icon('CaretRight');
export const Compass = icon('Compass');
export const Copy = icon('Copy');
export const CornerDownLeft = icon('ArrowBendDownLeft');
export const CornerUpLeft = icon('ArrowBendUpLeft');
export const Crown = icon('Crown', { weight: 'fill' });
export const Download = icon('DownloadSimple');
export const Eye = icon('Eye');
export const EyeOff = icon('EyeSlash');
export const FileText = icon('FileText');
export const Flag = icon('Flag');
export const FolderPlus = icon('FolderPlus');
export const Gavel = icon('Gavel', { weight: 'fill' });
export const Hash = icon('Hash');
export const HeadphoneOff = slashed('Headphones');
export const Headphones = icon('Headphones');
export const ImagePlus = plussed('ImageSquare');
export const Info = icon('Info');
export const KeyRound = icon('Key');
export const Keyboard = icon('Keyboard');
export const Link2 = icon('LinkSimple');
export const Lock = icon('Lock');
export const Poll = icon('ChartBarHorizontal');
export const Flask = icon('Flask', { weight: 'fill' });
export const Timer = icon('Timer');
export const AuditLog = icon('ListMagnifyingGlass');
export const Threads = icon('ChatsTeardrop');
export const Archive = icon('Archive');
export const Sticker = icon('Sticker');
export const LogOut = icon('SignOut');
export const Mail = icon('EnvelopeSimple');
export const Maximize = icon('CornersOut');
export const Menu = icon('List');
export const MessageCircle = icon('ChatCircle');
export const MessageCircleOff = icon('ChatCircleSlash');
export const MessagesSquare = icon('Chats');
export const Mic = icon('Microphone');
export const MicOff = icon('MicrophoneSlash');
export const Minimize = icon('CornersIn');
export const Monitor = icon('Monitor');
export const MoreHorizontal = icon('DotsThree');
export const Palette = icon('Palette');
export const PenLine = icon('PencilSimpleLine');
export const Pencil = icon('PencilSimple');
export const PhoneCall = icon('PhoneCall');
export const PhoneOff = icon('PhoneDisconnect');
export const Pin = icon('PushPin');
export const PinOff = icon('PushPinSlash');
export const Plus = icon('Plus');
export const PlusCircle = icon('PlusCircle');
export const RefreshCw = icon('ArrowsClockwise');
export const RotateCcw = icon('ArrowCounterClockwise');
export const ScreenShare = icon('MonitorArrowUp');
export const ScreenShareOff = slashed('MonitorArrowUp');
export const Search = icon('MagnifyingGlass');
export const Send = icon('PaperPlaneTilt');
export const ServerCrash = icon('CloudX');
export const Settings = icon('GearSix');
export const Settings2 = icon('SlidersHorizontal');
export const Shield = icon('Shield');
export const ShieldCheck = icon('ShieldCheck');
export const ShieldX = icon('ShieldSlash');
export const Signal = icon('CellSignalFull');
export const Smile = icon('Smiley');
export const SmilePlus = plussed('Smiley');
export const Terminal = icon('TerminalWindow');
export const Trash2 = icon('Trash');
export const Undo2 = icon('ArrowUUpLeft');
export const Upload = icon('UploadSimple');
export const User = icon('User');
export const UserCheck = icon('UserCheck');
export const UserCircle = icon('UserCircle');
export const UserMinus = icon('UserMinus');
export const UserPlus = icon('UserPlus');
export const UserX = slashed('User');
export const Users = icon('Users');
export const Video = icon('VideoCamera');
export const VideoOff = icon('VideoCameraSlash');
export const Volume2 = icon('SpeakerHigh');
export const VolumeX = icon('SpeakerSlash');
export const WifiOff = icon('WifiSlash');
export const X = icon('X');
export const Calendar = icon('CalendarDots');
export const Clock = icon('Clock');
export const MapPin = icon('MapPin');
export const Tag = icon('Tag');
export const Forum = icon('ChatsCircle');
export const Rules = icon('BookOpenText');
export const GoogleLogo = icon('GoogleLogo');
export const GithubLogo = icon('GithubLogo');
export const CalendarPlus = icon('CalendarPlus');
export const Bookmark = icon('BookmarkSimple');
export const BookmarkFilled = icon('BookmarkSimple', { weight: 'fill' });
export const AlarmClock = icon('Alarm');
export const History = icon('ClockCounterClockwise');
export const Play = icon('Play', { weight: 'fill' });
export const Pause = icon('Pause', { weight: 'fill' });
export const Stop = icon('Stop', { weight: 'fill' });
export const Megaphone = icon('Megaphone');
export const Forward = icon('ArrowBendUpRight');
export const Soundboard = icon('Waveform');
export const HandWaving = icon('HandWaving');
export const Sparkle = icon('Sparkle', { weight: 'fill' });
export const ListChecks = icon('ListChecks');
export const PickRoles = icon('IdentificationBadge');
export const Confetti = icon('Confetti');
/** ClanFire Blaze. */
export const Fire = icon('Fire', { weight: 'fill' });
/** Stokes (server boosts). */
export const Stoke = icon('FireSimple', { weight: 'fill' });
/** The Founder badge. */
export const Campfire = icon('Campfire', { weight: 'fill' });
export const Gift = icon('Gift');
export const ExternalLink = icon('ArrowSquareOut');
